import { blockCanGenerateSearch } from "#next/components/search-builder/Block";
import { TGetSavedSearch } from "#next/server/routers/savedSearch";
import { TFilterSettings } from "#next/utils/shared";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep, sortBy } from "lodash";
import { RootState } from "./reduxStore";

interface IBlockBaseUI {
  exclude: boolean;
  internalId: number; // No meaning, not persisted. Only exists to identify blocks on client side.
}

type BlockTypeWithoutGroup =
  | "SavedSearch"
  | "ExactText"
  | "StartsWith"
  | "Username";
export type BlockType = BlockTypeWithoutGroup | "Group";

export interface ISavedSearchBlockUI extends IBlockBaseUI {
  savedSearchId?: string;
  // TODO: Name might not be needed and could be derived from the ID at render time
  name?: string;
}

export interface ITextBlockUI extends IBlockBaseUI {
  isEditing: boolean;
  value?: string;
}

export interface IUsernameBlockUI extends IBlockBaseUI {
  isEditing: boolean;
  value?: string;
}

export enum BooleanMode {
  And = "And",
  Or = "Or",
}
export interface IGroupBlockUI extends IBlockBaseUI {
  booleanMode: BooleanMode;
  isRoot: boolean;
  textBlocks: ITextBlockUI[];
  startsWithBlocks: ITextBlockUI[];
  savedSearchBlocks: ISavedSearchBlockUI[];
  groupBlocks: IGroupBlockUI[];
  usernameBlocks: ITextBlockUI[];
}

export interface SearchBuilderUIState {
  currentRoot: IGroupBlockUI;
  undoRoots: IGroupBlockUI[];
  redoRoots: IGroupBlockUI[];
  hasUnsavedStructureOrFilterChanges: boolean;
  searchName: string | null;
  // This is only set when search has been saved
  originalSearchName: string | null;
  savedSearchId: string | null;
  filterSettings:
    | {
        settings: TFilterSettings;
        settingsAreValid: boolean;
      }
    | { settings: null; settingsAreValid: false };
  contentHash: string | null;
}

export interface SearchBuilderState {
  view: {
    [key: string]: SearchBuilderUIState;
  };
  edit: {
    [key: string]: SearchBuilderUIState;
  };
  newSearch: SearchBuilderUIState;
}

export type TSearchBuilderMode = keyof Pick<
  SearchBuilderState,
  "edit" | "view"
>;

const nextInternalID = (() => {
  let id = 0;
  return () => {
    id += 1;
    return id;
  };
})();

const _commonBlockProps = () => ({
  internalId: nextInternalID(),
  exclude: false,
});

const createTextBlock = (isEditing: boolean): ITextBlockUI => {
  return {
    ..._commonBlockProps(),
    isEditing,
  };
};

const createGroupBlock = (): IGroupBlockUI => {
  return {
    ..._commonBlockProps(),
    booleanMode: BooleanMode.Or,
    isRoot: false,
    textBlocks: [],
    savedSearchBlocks: [],
    groupBlocks: [],
    startsWithBlocks: [],
    usernameBlocks: [],
  };
};

const createSavedSearchBlock = (): ISavedSearchBlockUI => {
  return {
    ..._commonBlockProps(),
  };
};

function getEmptyUIState(): SearchBuilderUIState {
  const root = createGroupBlock();
  root.booleanMode = BooleanMode.And;
  root.isRoot = true;
  const innerGroup = createGroupBlock();
  innerGroup.textBlocks.push(createTextBlock(true));
  root.groupBlocks.push(innerGroup);
  return {
    currentRoot: root,
    undoRoots: [],
    redoRoots: [],
    hasUnsavedStructureOrFilterChanges: false,
    originalSearchName: null,
    searchName: null,
    savedSearchId: null,
    filterSettings: { settings: null, settingsAreValid: false },
    contentHash: null,
  };
}

const initialState = (): SearchBuilderState => {
  return { view: {}, edit: {}, newSearch: getEmptyUIState() };
};

function findBlock(
  group: IGroupBlockUI,
  key: "textBlocks",
  id: number,
): ITextBlockUI;
function findBlock(
  group: IGroupBlockUI,
  key: "startsWithBlocks",
  id: number,
): ITextBlockUI;
function findBlock(
  group: IGroupBlockUI,
  key: "savedSearchBlocks",
  id: number,
): ISavedSearchBlockUI;
function findBlock(
  group: IGroupBlockUI,
  key: "usernameBlocks",
  id: number,
): IUsernameBlockUI;
function findBlock(
  group: IGroupBlockUI,
  key: keyof Pick<
    IGroupBlockUI,
    "textBlocks" | "savedSearchBlocks" | "startsWithBlocks" | "usernameBlocks"
  >,
  id: number,
): ITextBlockUI | ISavedSearchBlockUI {
  const find = (_group: IGroupBlockUI): IGroupBlockUI[typeof key][0] | null => {
    // Casts are to avoid https://github.com/microsoft/TypeScript/issues/44373
    const blocks = _group[key] as IBlockBaseUI[];
    const found = blocks.find((block) => block.internalId === id);
    if (found) {
      return found as IGroupBlockUI[typeof key][0];
    }
    for (const gb of _group.groupBlocks) {
      const block = find(gb);
      if (block) {
        return block;
      }
    }
    return null;
  };

  const block = find(group);
  if (!block) {
    throw new Error(`Did not find block of type "${key}" with id ${id}`);
  }
  return block;
}

function findGroupBlock(group: IGroupBlockUI, id: number) {
  const find = (
    _group: IGroupBlockUI,
    depth: number,
  ): [IGroupBlockUI | null, number] => {
    if (_group.internalId === id) {
      return [_group, depth];
    }
    const found = _group.groupBlocks.find((tb) => tb.internalId === id);
    if (found) {
      return [found, depth];
    }
    for (const gb of _group.groupBlocks) {
      const [block, foundAtDepth] = find(gb, depth + 1);
      if (block) {
        return [block, foundAtDepth];
      }
    }
    return [null, depth];
  };

  const [block, depth] = find(group, 1);
  if (!block) {
    throw new Error(`Did not find Group Block with id ${id}`);
  }
  return { block, depth };
}

const findParentByChildId = (
  startingBlock: IGroupBlockUI,
  childId: number,
): IGroupBlockUI => {
  if (startingBlock.internalId === childId) {
    throw new Error("Child ID can't be same as ID of startingBlock");
  }

  const find = (block: IGroupBlockUI): IGroupBlockUI | undefined => {
    for (const child of allChildren(block)) {
      if (child.internalId === childId) {
        return block;
      }
    }
    for (const child of block.groupBlocks) {
      const found = find(child);
      if (found) {
        return found;
      }
    }
  };

  const result = find(startingBlock);
  if (!result) {
    throw new Error(`Parent of block id ${childId} not found`);
  }
  return result;
};

const hasAnyChildren = (groupBlock: IGroupBlockUI) => {
  return (
    groupBlock.textBlocks.length ||
    groupBlock.startsWithBlocks.length ||
    groupBlock.savedSearchBlocks.length ||
    groupBlock.groupBlocks.length
  );
};

export const allChildren = (groupBlock: IGroupBlockUI) => {
  return [
    ...groupBlock.textBlocks,
    ...groupBlock.startsWithBlocks,
    ...groupBlock.savedSearchBlocks,
    ...groupBlock.groupBlocks,
    ...groupBlock.usernameBlocks,
  ];
};

// TODO: Remove all undo/redo stuff
const saveUndo = (state: SearchBuilderUIState) => {
  state.undoRoots.push(state.currentRoot);
  state.currentRoot = cloneDeep(state.currentRoot);
  state.redoRoots = [];
  // We're not trying to be smart about tracking changes
  state.hasUnsavedStructureOrFilterChanges = true;
};

type TSavedSearchId = string | null;

export const searchBuilderSlice = createSlice({
  name: "search builder",
  initialState,
  reducers: {
    setExclude: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        blockId: number;
        exclude: boolean;
        blockType: BlockType;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      saveUndo(search);
      const { blockId, exclude } = action.payload;
      switch (action.payload.blockType) {
        case "Group":
          findGroupBlock(search.currentRoot, blockId).block.exclude = exclude;
          break;
        case "SavedSearch":
          findBlock(search.currentRoot, "savedSearchBlocks", blockId).exclude =
            exclude;
          break;
        case "ExactText":
          findBlock(search.currentRoot, "textBlocks", blockId).exclude =
            exclude;
          break;
        case "StartsWith":
          findBlock(search.currentRoot, "startsWithBlocks", blockId).exclude =
            exclude;
          break;
        case "Username":
          findBlock(search.currentRoot, "usernameBlocks", blockId).exclude =
            exclude;
          break;
        default:
          const _exhaustiveCheck: never = action.payload.blockType;
          return _exhaustiveCheck;
      }
    },
    setText: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        blockId: number;
        blockType: "ExactText" | "StartsWith" | "Username";
        text: string;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      saveUndo(search);
      let block: ITextBlockUI;
      switch (action.payload.blockType) {
        case "ExactText":
          block = findBlock(
            search.currentRoot,
            "textBlocks",
            action.payload.blockId,
          );
          break;
        case "StartsWith":
          block = findBlock(
            search.currentRoot,
            "startsWithBlocks",
            action.payload.blockId,
          );
          break;
        case "Username":
          block = findBlock(
            search.currentRoot,
            "usernameBlocks",
            action.payload.blockId,
          );
          break;
        default:
          const _exhaustivenessCheck: never = action.payload.blockType;
          return _exhaustivenessCheck;
      }
      block.value = action.payload.text;
      block.isEditing = false;
    },
    addChild: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        blockId: number;
        childType: BlockType;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      saveUndo(search);
      const { block } = findGroupBlock(
        search.currentRoot,
        action.payload.blockId,
      );
      switch (action.payload.childType) {
        case "Group":
          block.groupBlocks.push(createGroupBlock());
          break;
        case "SavedSearch":
          block.savedSearchBlocks.push(createSavedSearchBlock());
          break;
        case "ExactText":
          block.textBlocks.push(createTextBlock(true));
          break;
        case "StartsWith":
          block.startsWithBlocks.push(createTextBlock(true));
          break;
        case "Username":
          block.usernameBlocks.push(createTextBlock(true));
          break;
        default:
          const _exhaustiveCheck: never = action.payload.childType;
          return _exhaustiveCheck;
      }
    },
    setGroupBooleanMode: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        blockId: number;
        booleanMode: BooleanMode;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      const { block } = findGroupBlock(
        search.currentRoot,
        action.payload.blockId,
      );
      block.booleanMode = action.payload.booleanMode;
      if (block.booleanMode === BooleanMode.Or) {
        allChildren(block).forEach((child) => (child.exclude = false));
      }
      search.hasUnsavedStructureOrFilterChanges = true;
    },
    deleteBlock: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        blockId: number;
        blockType: BlockType;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      saveUndo(search);
      const { blockId, blockType } = action.payload;
      if (blockId === search.currentRoot.internalId) {
        throw new Error("Cannot delete root block");
      }
      const parent = findParentByChildId(search.currentRoot, blockId);
      switch (blockType) {
        case "Group":
          parent.groupBlocks = parent.groupBlocks.filter(
            (block) => block.internalId !== blockId,
          );
          break;
        case "SavedSearch":
          parent.savedSearchBlocks = parent.savedSearchBlocks.filter(
            (block) => block.internalId !== blockId,
          );
          break;
        case "ExactText":
          parent.textBlocks = parent.textBlocks.filter(
            (block) => block.internalId !== blockId,
          );
          break;
        case "StartsWith":
          parent.startsWithBlocks = parent.startsWithBlocks.filter(
            (block) => block.internalId !== blockId,
          );
          break;
        case "Username":
          parent.usernameBlocks = parent.usernameBlocks.filter(
            (block) => block.internalId !== blockId,
          );
          break;
        default:
          const _exhaustiveCheck: never = blockType;
          return _exhaustiveCheck;
      }
      const children = allChildren(parent);
      if (children.length === 1) {
        if (!children[0]) {
          throw new Error(`Should have found child at children[0] ${children}`);
        }
        children[0].exclude = false;
      }
      if (!hasAnyChildren(parent)) {
        parent.exclude = false;
      }
    },
    setSearchWasUpdated: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      search.hasUnsavedStructureOrFilterChanges = false;
      search.originalSearchName = search.searchName;
    },
    setSearchWasCreated: (state) => {
      state.newSearch.hasUnsavedStructureOrFilterChanges = false;
    },
    setIsEditing: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        blockType: "ExactText" | "StartsWith" | "Username";
        blockId: number;
        isEditing: boolean;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      saveUndo(search);
      const { isEditing, blockType, blockId } = action.payload;
      if (blockType === "ExactText") {
        findBlock(search.currentRoot, "textBlocks", blockId).isEditing =
          isEditing;
      } else if (blockType === "StartsWith") {
        findBlock(search.currentRoot, "startsWithBlocks", blockId).isEditing =
          isEditing;
      } else if (blockType === "Username") {
        findBlock(search.currentRoot, "usernameBlocks", blockId).isEditing =
          isEditing;
      }
    },
    setReferencedSavedSearchId: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        referencedSavedSearchId: string;
        blockId: number;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      saveUndo(search);
      const block = findBlock(
        search.currentRoot,
        "savedSearchBlocks",
        action.payload.blockId,
      );
      block.savedSearchId = action.payload.referencedSavedSearchId;
    },
    undo: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      const newCurrent = search.undoRoots.pop();
      if (!newCurrent) {
        throw new Error("Tried to undo but nothing in history");
      }
      if (search.undoRoots.length === 0) {
        search.hasUnsavedStructureOrFilterChanges = false;
      }
      search.redoRoots = [search.currentRoot, ...search.redoRoots];
      search.currentRoot = newCurrent;
    },
    redo: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);

      const newCurrent = search.redoRoots.shift();
      if (!newCurrent) {
        throw new Error("Tried to redo but nothing in history");
      }
      search.undoRoots.push(search.currentRoot);
      search.currentRoot = newCurrent;
    },
    clearState: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      Object.assign(search, getEmptyUIState());
    },
    resetNewSearch: (
      state,
      action: PayloadAction<{ initialFilterSettings: TFilterSettings }>,
    ) => {
      state.newSearch = getEmptyUIState();
      state.newSearch.filterSettings = {
        settings: action.payload.initialFilterSettings,
        settingsAreValid: true,
      };
    },
    setSearchName: (
      state,
      action: PayloadAction<{ name: string; savedSearchId: TSavedSearchId }>,
    ) => {
      const search = getSearchForReducer(state, action);
      search.searchName = action.payload.name;
    },
    setFilterSettings: (
      state,
      action: PayloadAction<{
        savedSearchId: TSavedSearchId;
        settings: TFilterSettings;
        settingsAreValid: boolean;
      }>,
    ) => {
      const search = getSearchForReducer(state, action);
      search.filterSettings = {
        settings: action.payload.settings,
        settingsAreValid: action.payload.settingsAreValid,
      };
      search.hasUnsavedStructureOrFilterChanges =
        search.hasUnsavedStructureOrFilterChanges ||
        action.payload.settingsAreValid;
    },
    // Usually with Redux you would use thunks or similar to fetch data into the store, but
    // we're using tRPC/react-query for all server-side state. So this function allows
    // the response from tRPC's useQuery to set a new Redux store state for search builder.
    setStateFromBackend: (
      state,
      action: PayloadAction<{
        mode: TSearchBuilderMode;
        // Redux doesn't want non-serializable values in the store (Date objects in this case)
        // and has no use for these timestamps anyway
        savedSearch: Omit<TGetSavedSearch, "createdAt" | "updatedAt">;
        contentHash: string;
      }>,
    ) => {
      const convertToReduxForm = (
        groupBlock: TGetSavedSearch["groupBlock"],
      ): IGroupBlockUI => {
        const textBlocks: ITextBlockUI[] = groupBlock.textBlocks.map(
          (block) => ({
            value: block.value,
            exclude: block.exclude,
            internalId: nextInternalID(),
            isEditing: false,
          }),
        );
        const startsWithBlocks: ITextBlockUI[] =
          groupBlock.startsWithBlocks.map((block) => ({
            value: block.value,
            exclude: block.exclude,
            internalId: nextInternalID(),
            isEditing: false,
          }));
        const savedSearchBlocks: ISavedSearchBlockUI[] =
          groupBlock.savedSearchBlocks.map((block) => ({
            exclude: block.exclude,
            savedSearchId: block.savedSearchId,
            internalId: nextInternalID(),
          }));
        const usernameBlocks: IUsernameBlockUI[] =
          groupBlock.usernameBlocks.map((block) => ({
            value: block.value,
            exclude: block.exclude,
            internalId: nextInternalID(),
            isEditing: false,
          }));
        const ret: Omit<IGroupBlockUI, "groupBlocks"> = {
          exclude: groupBlock.exclude,
          // TODO: Seems like this should pass through. Some difference between the client-side enum and the DB enum?
          booleanMode:
            groupBlock.booleanMode === "And" ? BooleanMode.And : BooleanMode.Or,
          internalId: nextInternalID(),
          isRoot: groupBlock.parentGroupId === null,
          savedSearchBlocks,
          textBlocks: sortBy(
            textBlocks,
            (block) => `${block.exclude ? "1" : "0"} ${block.value}`, // Hacky way to put non-excluded blocks before excluded
          ),
          startsWithBlocks: sortBy(
            startsWithBlocks,
            (block) => `${block.exclude ? "1" : "0"} ${block.value}`, // Hacky way to put non-excluded blocks before excluded
          ),
          usernameBlocks: sortBy(
            usernameBlocks,
            (block) => `${block.exclude ? "1" : "0"} ${block.value}`, // Hacky way to put non-excluded blocks before excluded
          ),
        };
        return {
          ...ret,
          groupBlocks: groupBlock.groupBlocks.length
            ? groupBlock.groupBlocks.map((block) => convertToReduxForm(block))
            : [],
        };
      };

      const { savedSearch, mode } = action.payload;

      const existingSearchState = state[mode][savedSearch.id];
      if (
        existingSearchState &&
        existingSearchState.hasUnsavedStructureOrFilterChanges
      ) {
        if (mode === "view") {
          throw new Error(
            `It should be impossible for a SavedSearch in view mode to have unsaved edits. ID ${savedSearch.id}`,
          );
        }
        console.warn(
          `Rejecting setStateFromBackend on Saved Search ${savedSearch.id} as local copy has unsaved changes. Try to prevent this from happening (sometimes it may be unavoidable due to some component rerendering?)`,
        );
        return;
      }

      const searchState: SearchBuilderUIState = {
        hasUnsavedStructureOrFilterChanges: false,
        currentRoot: convertToReduxForm(savedSearch.groupBlock),
        redoRoots: [],
        undoRoots: [],
        searchName: savedSearch.name,
        originalSearchName: savedSearch.name,
        savedSearchId: savedSearch.id,
        filterSettings: {
          settings: savedSearch.filterSettings,
          settingsAreValid: true,
        },
        contentHash: action.payload.contentHash,
      };
      state[mode][savedSearch.id] = searchState;
    },
  },
});

export const {
  setExclude,
  setText,
  addChild,
  deleteBlock,
  setIsEditing,
  setReferencedSavedSearchId,
  setGroupBooleanMode,
  undo,
  redo,
  setStateFromBackend,
  clearState,
  setSearchName,
  setSearchWasUpdated,
  setSearchWasCreated,
  setFilterSettings,
  resetNewSearch,
} = searchBuilderSlice.actions;

export const reducer = searchBuilderSlice.reducer;

function _selectBlock(
  state: SearchBuilderUIState,
  blockId: number,
  blockType: "SavedSearch",
): ISavedSearchBlockUI;
function _selectBlock(
  state: SearchBuilderUIState,
  blockId: number,
  blockType: "ExactText",
): ITextBlockUI;
function _selectBlock(
  state: SearchBuilderUIState,
  blockId: number,
  blockType: "StartsWith",
): ITextBlockUI;
function _selectBlock(
  state: SearchBuilderUIState,
  blockId: number,
  blockType: "Username",
): ITextBlockUI;
function _selectBlock(
  state: SearchBuilderUIState,
  blockId: number,
  blockType: BlockTypeWithoutGroup,
) {
  const groupBlock = state.currentRoot;
  switch (blockType) {
    case "SavedSearch":
      return findBlock(groupBlock, "savedSearchBlocks", blockId);
    case "ExactText":
      return findBlock(groupBlock, "textBlocks", blockId);
    case "StartsWith":
      return findBlock(groupBlock, "startsWithBlocks", blockId);
    case "Username":
      return findBlock(groupBlock, "usernameBlocks", blockId);
    default:
      const _exhaustiveCheck: never = blockType;
      return _exhaustiveCheck;
  }
}

export const selectGroupBlock = createSelector(
  /* It's a bit ugly how this needs a second selector that just returns blockId in order to pass
   that along to the result function, but this is straight from the docs
   https://reselect.js.org/faq/#how-do-i-create-a-selector-that-takes-an-argument
  */
  [
    (state: SearchBuilderUIState) => state.currentRoot,
    (state: SearchBuilderUIState, blockId: number) => blockId,
  ],
  (currentRoot: IGroupBlockUI, blockId: number) => {
    return findGroupBlock(currentRoot, blockId);
  },
);

export const selectPeerTextBlocksContent = createSelector(
  [
    (state: SearchBuilderUIState) => state.currentRoot,
    (state: SearchBuilderUIState, blockId: number) => blockId,
    (
      state: SearchBuilderUIState,
      blockId: number,
      type: "textBlocks" | "startsWithBlocks" | "usernameBlocks",
    ) => type,
  ],
  (currentRoot, blockId, type) => {
    const parent = findParentByChildId(currentRoot, blockId);
    return parent[type]
      .filter((block) => block.internalId !== blockId)
      .map((block) => block.value)
      .flatMap((value) => value ?? []);
  },
);

export const selectBlockParent = (
  state: SearchBuilderUIState,
  blockId: number,
) => {
  return findParentByChildId(state.currentRoot, blockId);
};

export const selectBlock = _selectBlock;

export const getSearchFromState = <T extends SearchBuilderState>(
  state: T,
  mode: TSearchBuilderMode,
  savedSearchId: string | null,
) => {
  let uiState: SearchBuilderUIState | undefined;
  if (savedSearchId === null) {
    uiState = state.newSearch;
  } else {
    uiState = state[mode][savedSearchId];
    if (!uiState) {
      throw new Error(
        `No search at state.searchBuilderReducer['${savedSearchId}"]`,
      );
    }
  }
  return uiState;
};

function getSearchForReducer<
  T extends PayloadAction<{ savedSearchId: TSavedSearchId }>,
>(state: SearchBuilderState, action: T) {
  return getSearchFromState(state, "edit", action.payload.savedSearchId);
}

export const selectHasUnsavedChanges = (state: SearchBuilderUIState) =>
  state.hasUnsavedStructureOrFilterChanges;

export const selectCanSave = (state: SearchBuilderUIState) => {
  const { canSearch } = blockCanGenerateSearch(
    state.currentRoot,
    state.filterSettings.settingsAreValid,
  );
  const nameHasChanged = state.originalSearchName !== state.searchName;
  // It's possible to save an effectively-unmodified search by adding
  // a block and then deleting it. Catching that this results in the
  // same search as we started with would require something like
  // storing the structure of the original search and then diffing the
  // current one with it (could compute a hash of all properties). We could
  // implement it later if needed.
  const filterIsValid = state.filterSettings.settingsAreValid;
  return (
    canSearch &&
    filterIsValid &&
    (nameHasChanged || state.hasUnsavedStructureOrFilterChanges)
  );
};

export const selectSearchName = (state: SearchBuilderUIState) =>
  state.searchName;

export const selectFilterSettings = (state: SearchBuilderUIState) =>
  state.filterSettings;

export const selectRootBlock = (state: SearchBuilderUIState) =>
  state.currentRoot;

const findNonGroupNodeInTree = (startingBlock: IGroupBlockUI) => {
  const find = (block: IGroupBlockUI) => {
    if (block.textBlocks.length || block.groupBlocks.length) {
      return true;
    }
    for (const child of block.groupBlocks) {
      const found = find(child);
      if (found) {
        return true;
      }
    }
    return false;
  };
  return find(startingBlock);
};

export const selectCanExcludeTextBlock = (
  state: SearchBuilderUIState,
  block: ITextBlockUI,
) => _getBlockExclusionDisabledReason(state, block);

export const selectSavedSearchId = (
  state: RootState,
  mode: TSearchBuilderMode,
) => state.searchBuilderReducer[mode].savedSearchId;

export const selectCanExcludeSavedSearchBlock = (
  state: SearchBuilderUIState,
  block: ISavedSearchBlockUI,
) => _getBlockExclusionDisabledReason(state, block);

const _getBlockExclusionDisabledReason = (
  state: SearchBuilderUIState,
  block: IBlockBaseUI,
) => {
  const parent = findParentByChildId(state.currentRoot, block.internalId);
  if (parent.booleanMode === BooleanMode.Or) {
    return 'a block can only be excluded when its Group block is set to "every block"';
  }
  // If this block is currently excluded then it can be toggled.
  if (block.exclude) {
    return null;
  }
  const children = allChildren(parent);
  if (
    children.filter((child) => child.exclude).length ===
    children.length - 1
  ) {
    return "at least one block in this Group block must be included";
  }

  return null;
};

export const selectGroupBlockExclusionDisabledReason = (
  state: SearchBuilderUIState,
  block: IGroupBlockUI,
) => {
  if (block.isRoot) {
    return "you cannot exclude the top-level Group block";
  }
  if (!hasAnyChildren(block)) {
    return "you cannot exclude an empty Group block";
  }

  const result = _getBlockExclusionDisabledReason(state, block);
  if (result) {
    return result;
  }

  const subtreeContainsNonGroupBlock = findNonGroupNodeInTree(block);

  if (subtreeContainsNonGroupBlock) {
    return null;
  }
  return "all child blocks are empty Group blocks";
};
